import { createTransactionAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/createTransactionAnnouncement'
import { queryAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/queryAnnouncement'
import { updateTransactionAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/updateTransactionAnnouncement'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { SubmitType } from '@eastcoal/formily/components/CrudForm/enums/SubmitType'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { RefreshButton } from '@eastcoal/formily/components/CrudForm/shared/RefreshButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { RichEditor } from '@eastcoal/formily/components/RichEditor'
import { TextArea } from '@eastcoal/formily/components/TextArea'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { AnnouncementSelect } from '@eastcoal/shared/@formily/AnnouncementSelect'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'
import { MemberSelect } from '@eastcoal/shared/@formily/MemberSelect'

import { ChannelCascader } from '../../components/ChannelCascader'
import { AnnouncementStatus } from '../../enums/AnnouncementStatus'
import { ChannelType } from '../../enums/ChannelType'
import {
  ANNOUNCEMENT_FIELD,
  KIND_FIELD,
  MEMBER_FIELD
} from '../constants/field'

import { announcementEffect } from './announcementEffect'
import {
  TransactionAnnouncementPageFormProps,
  TransactionAnnouncementFormProps
} from './interface'

const schema: TransactionAnnouncementPageFormProps['schema'] = [
  {
    name: 'title',
    title: '文章标题',
    type: 'string',
    required: true,
    'x-decorator': FormItem,
    'x-decorator-props': {
      gridSpan: 2
    },
    'x-component': Input,
    'x-component-props': {
      maxLength: 50,
      showCount: true,
      placeholder: '请输入标题，限50个字符以内'
    }
  },
  {
    name: '{id: channelId,name: channelName}',
    title: '所属频道',
    type: 'object',
    required: true,
    'x-decorator': FormItem,
    'x-component': ChannelCascader,
    'x-component-props': {
      type: ChannelType.TRANSACTION_ANNOUNCEMENT
    }
  },
  {
    name: MEMBER_FIELD,
    title: '委托方',
    required: true,
    type: 'object',
    'x-decorator': FormItem,
    'x-component': MemberSelect
  },
  {
    name: KIND_FIELD,
    title: '品种',
    required: true,
    type: 'object',
    'x-decorator': FormItem,
    'x-component': KindAllNodeCascader
  },
  {
    name: ANNOUNCEMENT_FIELD,
    title: '竞价预告',
    required: true,
    type: 'object',
    'x-decorator': FormItem,
    'x-component': AnnouncementSelect,
    'x-component-props': {
      placeholder: '请先选择会员和品种'
    }
  },
  {
    name: 'description',
    title: '文章摘要',
    type: 'string',
    'x-decorator': FormItem,
    'x-decorator-props': {
      gridSpan: 2
    },
    'x-component': TextArea,
    'x-component-props': {
      maxLength: 300,
      showCount: true,
      placeholder: '请输入文章摘要，限300个字符以内'
    }
  },
  {
    name: 'content',
    title: '文章正文',
    type: 'string',
    'x-decorator': FormItem,
    'x-decorator-props': {
      gridSpan: 2
    },
    'x-component': RichEditor
  }
]

const readApi: TransactionAnnouncementPageFormProps['readApi'] = ({ id }) => {
  return {
    api: () => queryAnnouncementApi({ id }),
    ready: id
  }
}

const createApi: TransactionAnnouncementPageFormProps['createApi'] = (
  values,
  { submitType }
) => {
  return createTransactionAnnouncementApi({
    ...values,
    status:
      submitType === SubmitType.SUBMIT
        ? AnnouncementStatus.PUBLISHED
        : AnnouncementStatus.SAVED
  })
}

const updateApi: TransactionAnnouncementPageFormProps['updateApi'] = (
  values,
  { submitType }
) => {
  return updateTransactionAnnouncementApi({
    ...values,
    status:
      submitType === SubmitType.SUBMIT
        ? AnnouncementStatus.PUBLISHED
        : AnnouncementStatus.SAVED
  })
}

export const TransactionForm = ({
  mode,
  values
}: TransactionAnnouncementFormProps) => {
  const navigate = useNavigate()
  const { message } = useFeedback()

  const handleSubmitSuccess = () => {
    message.success('操作成功')
    navigate(-1)
  }

  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        mode={mode}
        name='transactionAnnouncementForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        values={values}
        actions={[
          <BackButton key='back' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='submitButton'>发布</SubmitButton>,
          <RefreshButton key='refreshButton' />
        ]}
        effects={() => {
          announcementEffect()
        }}
        grid={{
          columns: 2
        }}
        onSuccess={handleSubmitSuccess}
      />
    </PageContainer>
  )
}
